/**
 * @fileoverview Submenu course link component for the courses menu. This
 * component is used to render the course links in the submenu. These links are
 * key accessible and have hover and focus states. These refs are populated in
 * the subItemRefs object for keyboard navigation. It also includes the new pill
 * and subject percentage pill if the course is new or has a subject percentage.
 */
import {css, StyleSheet} from "aphrodite";
import React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import Link from "@khanacademy/wonder-blocks-link";
import {
    semanticColor,
    sizing,
    spacing,
} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {useLocation} from "@khan/urls";

import NewBadge from "../new-badge.tsx";
import type {DomainLink} from "../types.ts";

import {SubjectPercentagePill} from "./subject-percentage-pill.tsx";

type Props = {
    // The course link in the submenu
    item: DomainLink;
    // The ref object for the sub-menu items
    subItemRefs: React.MutableRefObject<object>;
    // The index of the sub-menu item (2-tier) or curation page group (3-tier) for subItemRefs
    index: number;
    // Clicking link should close the learn menu under AppShell.
    onClickLink: () => void;
    // Subject percentage for subject
    subjectPercentage?: number;
};

export const SubMenuCourseLink = ({
    item,
    subItemRefs,
    index,
    onClickLink,
    subjectPercentage,
}: Props) => {
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const location = useLocation();

    const handleClick = async () => {
        await publishAnalyticsEvent({
            eventType: "NavBarCourseMenuItemClickedTI",
            eventSchemaVersion: 1,
            currentPageUrlPath: location.pathname,
            contentPath: item.href,
            courseId: item.courseId ?? "",
        });
        onClickLink();
    };

    return (
        <Link
            role="menuitem"
            safeWithNav={handleClick}
            // For "non-content" links, we don't expect to be able to
            // use client-side navigation, so force a full page reload.
            skipClientNav={item.nonContentLink}
            href={item.href}
            style={styles.submenuItem}
            ref={(node: HTMLAnchorElement) => {
                if (node) {
                    subItemRefs.current[index] = node;
                }
            }}
            tabIndex={-1}
            testId={item.slug}
        >
            <View className={css(styles.submenuItemWrapper)}>
                <LabelMedium tag="span" style={styles.submenuItemTitle}>
                    {item.translatedTitle}
                    {/* New pill inline */}
                    {item.isNew && <NewBadge kind="info" testId="new-pill" />}
                    {/* Subject percentages inline */}
                    {subjectPercentage ? (
                        <SubjectPercentagePill
                            slug={item.slug}
                            subjectPercentage={subjectPercentage}
                        />
                    ) : null}
                </LabelMedium>
            </View>
        </Link>
    );
};

const styles = StyleSheet.create({
    submenuItem: {
        display: "block",
        paddingTop: sizing.size_080,
        paddingBottom: sizing.size_080,
        paddingLeft: sizing.size_120,
        paddingRight: sizing.size_120,
        color: semanticColor.core.foreground.neutral.strong,
        ":hover": {
            backgroundColor:
                semanticColor.chonky.progressive.background.secondary.hover,
            color: semanticColor.link.press,
            borderRadius: sizing.size_040,
            border: "1px transparent #000",
            textDecoration: "none",
        },
        ":focus": {
            backgroundColor:
                semanticColor.chonky.progressive.background.secondary.hover,
            color: semanticColor.link.press,
            borderRadius: sizing.size_040,
            border: "1px transparent #000",
            textDecoration: "none",
        },
        [":hover [data-testid=new-pill] " as any]: {
            backgroundColor:
                semanticColor.action.primary.progressive.hover.background,
            color: semanticColor.chonky.progressive.foreground.primary.hover,
        },
        [":focus [data-testid=new-pill] " as any]: {
            backgroundColor:
                semanticColor.action.primary.progressive.hover.background,
            color: semanticColor.chonky.progressive.foreground.primary.hover,
        },
        [":hover [data-testid=subject-percentage-pill] " as any]: {
            backgroundColor:
                semanticColor.action.primary.progressive.hover.background,
            color: semanticColor.chonky.progressive.foreground.primary.hover,
        },
        [":focus [data-testid=subject-percentage-pill] " as any]: {
            backgroundColor:
                semanticColor.action.primary.progressive.hover.background,
            color: semanticColor.chonky.progressive.foreground.primary.hover,
        },
    },
    submenuItemWrapper: {
        display: "flex",
        alignItems: "flex-start",
    },
    submenuItemTitle: {
        display: "flex",
        alignItems: "baseline", // Align items to text baseline
        gap: spacing.xxxSmall_4, // Small gap between text and NEW badge
    },
});
